import React, { useRef } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import ReactQuill from "react-quill";

import appStyles from "../../App.module.css";
import btnStyles from "../../styles/Button.module.css";
import styles from "../../styles/SupportTickets.module.css";

import "quill/dist/quill.snow.css";

const supportPrimaryBtnClass = `${styles.SupportBtn} ${styles.SupportPrimaryBtn}`;

const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const CreateTicketModal = ({
  show,
  onHide,
  createForm,
  onChange,
  onSubmit,
  isSubmitting,
  categories,
  isLoadingCategories,
  attachmentFiles = [],
  onAttachmentsAdd,
  onAttachmentRemove,
}) => {
  const categoriesEmpty = !Array.isArray(categories) || categories.length === 0;
  const attachmentFileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const { files } = event.target;
    if (files?.length) {
      onAttachmentsAdd?.(files);
    }
    // Reset so the same file(s) can be chosen again after removing.
    event.target.value = "";
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      className={styles.SupportModal}
      dialogClassName={styles.ModalWide}
    >
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Raise a New Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.ModalBodyScroll}>
          <Form.Group controlId="ticketTitle">
            <Form.Label>
              Title<span className={styles.RequiredAsterisk}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={createForm.title}
              onChange={onChange}
              disabled={isSubmitting}
            />
          </Form.Group>
          <Form.Group controlId="ticketCategory">
            <Form.Label>
              Category<span className={styles.RequiredAsterisk}>*</span>
            </Form.Label>
            <Form.Control
              as="select"
              name="categoryId"
              value={createForm.categoryId}
              onChange={onChange}
              disabled={isSubmitting || isLoadingCategories || categoriesEmpty}
            >
              {categoriesEmpty ? (
                <option value="">
                  {isLoadingCategories ? "Loading categories..." : "No categories available"}
                </option>
              ) : (
                categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))
              )}
            </Form.Control>
          </Form.Group>
          <Form.Group controlId="ticketDescription">
            <Form.Label>
              Description<span className={styles.RequiredAsterisk}>*</span>
            </Form.Label>
            <div className={`${styles.QuillEditor}`}>
              <ReactQuill
                theme="snow"
                value={createForm.description}
                onChange={(html) =>
                  onChange({
                    target: {
                      name: "description",
                      value: html,
                    },
                  })
                }
                placeholder="Describe your issue..."
                readOnly={isSubmitting}
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                  ],
                }}
                formats={["bold", "italic", "underline", "strike", "list", "link"]}
              />
            </div>
          </Form.Group>

          <Form.Group controlId="ticketAttachments">
            <Form.Label>Attachments (optional)</Form.Label>
            <Form.Text muted className="d-block mb-2">
              Images only. Multiple files allowed; each must be 5 MB or less.
            </Form.Text>
            <div className="d-flex align-items-center flex-wrap">
              <input
                ref={attachmentFileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="d-none"
                onChange={handleFileChange}
                disabled={isSubmitting}
                tabIndex={-1}
              />
              <Button
                type="button"
                variant="primary"
                className={supportPrimaryBtnClass}
                disabled={isSubmitting}
                onClick={() => attachmentFileInputRef.current?.click()}
              >
                Browse file
              </Button>
            </div>
            {attachmentFiles.length > 0 && (
              <div className={`${styles.ReplyStagedFiles} mt-3`}>
                {attachmentFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${file.lastModified}-${index}`}
                    className={styles.ReplyStagedFileRow}
                  >
                    <div className={styles.ReplyStagedFileMeta}>
                      <span className={styles.ReplyStagedFileName} title={file.name}>
                        {file.name}
                      </span>
                      <span className="text-muted small">{formatFileSize(file.size)}</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      className={styles.CreateAttachmentRemove}
                      disabled={isSubmitting}
                      onClick={() => onAttachmentRemove?.(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} className={styles.SupportBtn}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className={`${styles.SupportBtn} ${btnStyles.Button} ${btnStyles.Blue} ${appStyles.lato}`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;
